import type { DataTableColumnFilter } from "@frontend/components/organisms/DataTable";
import { request } from "@frontend/lib/http";

import type { ProjectValues } from "../schemas/project";
import type {
  BalanceSheet,
  BalanceSheetBillInput,
  ClientContactOption,
  ClosureWeekYear,
  ClosureWeekYearInput,
  ExpenseTypeOption,
  InterventionRow,
  InterventionState,
  JobRow,
  PaymentTypeOption,
  PickerOption,
  PrefillSource,
  Project,
  ProjectListColumn,
  ProjectListDirection,
  ProjectListFilter,
  ProjectPage,
  ProjectQuickview,
  ProjectRow,
  ProjectType,
} from "../types";
import { toProjectInput } from "../utils/payload";

const LOCATION_PAGE_CAP = 200;

type RequestOptions = {
  signal?: AbortSignal;
};

export type ProjectListQuery = {
  search?: string;
  type?: ProjectType | null;
  filter?: ProjectListFilter;
  page?: number;
  perPage?: number;
  sort?: ProjectListColumn | null;
  direction?: ProjectListDirection;
  criteria?: DataTableColumnFilter[];
  closureWeeksYear?: number | null;
  responsableId?: number | null;
};

export type ProjectApi = {
  listInterventionStates(
    options?: RequestOptions,
  ): Promise<InterventionState[]>;
  listProjects(
    query: ProjectListQuery,
    options?: RequestOptions,
  ): Promise<ProjectPage>;
  archiveProject(
    projectId: number,
    options?: RequestOptions,
  ): Promise<ProjectRow>;
  deleteProject(projectId: number, options?: RequestOptions): Promise<void>;
  fetchProject(projectId: number, options?: RequestOptions): Promise<Project>;
  fetchQuickview(
    projectId: number,
    options?: RequestOptions,
  ): Promise<ProjectQuickview>;
  createProject(
    type: ProjectType,
    input: ProjectValues,
    options?: RequestOptions,
  ): Promise<Project>;
  updateProject(
    projectId: number,
    input: ProjectValues,
    options?: RequestOptions,
  ): Promise<Project>;
  fetchJobs(projectId: number, options?: RequestOptions): Promise<JobRow[]>;
  fetchInterventions(
    projectId: number,
    options?: RequestOptions,
  ): Promise<InterventionRow[]>;
  fetchExpenseTypes(options?: RequestOptions): Promise<ExpenseTypeOption[]>;
  fetchPaymentTypes(options?: RequestOptions): Promise<PaymentTypeOption[]>;
  fetchClientSubscriptions(
    clientId: number,
    options?: RequestOptions,
  ): Promise<PickerOption[]>;
  fetchLocations(
    contactId: number,
    options?: RequestOptions,
  ): Promise<PickerOption[]>;
  fetchBillingAddresses(
    contactId: number,
    options?: RequestOptions,
  ): Promise<PickerOption[]>;
  fetchClientContacts(
    clientId: number,
    options?: RequestOptions,
  ): Promise<ClientContactOption[]>;
  fetchBalanceSheet(
    projectId: number,
    options?: RequestOptions,
  ): Promise<BalanceSheet>;
  saveBalanceSheetBill(
    projectId: number,
    input: BalanceSheetBillInput,
    options?: RequestOptions,
  ): Promise<BalanceSheet>;
  sendBalanceSheetToBilling(
    projectId: number,
    input: BalanceSheetBillInput,
    options?: RequestOptions,
  ): Promise<BalanceSheet>;
  closeBalanceSheet(
    projectId: number,
    input: BalanceSheetBillInput,
    options?: RequestOptions,
  ): Promise<BalanceSheet>;
  fetchClosureWeeks(
    projectId: number,
    year: number,
    prefill?: PrefillSource,
    options?: RequestOptions,
  ): Promise<ClosureWeekYear>;
  saveClosureWeeks(
    projectId: number,
    year: number,
    input: ClosureWeekYearInput,
    options?: RequestOptions,
  ): Promise<ClosureWeekYear>;
};

export const projectApi: ProjectApi = {
  listInterventionStates(options) {
    return request({ route: "api_projects_intervention_states", ...options });
  },

  listProjects(
    {
      search,
      type,
      filter,
      page,
      perPage,
      sort,
      direction,
      criteria,
      closureWeeksYear,
      responsableId,
    },
    options,
  ) {
    return request({
      route: "api_projects_list",
      query: {
        search: search || undefined,
        type: type ?? undefined,
        filter,
        page,
        perPage,
        sort: sort ?? undefined,
        direction: sort ? direction : undefined,
        filters: encodeCriteria(criteria),
        closureWeeksYear: closureWeeksYear ?? undefined,
        responsableId: responsableId ?? undefined,
      },
      ...options,
    });
  },

  fetchQuickview(projectId, options) {
    return request({
      route: "api_projects_quickview",
      params: { projectId },
      ...options,
    });
  },

  archiveProject(projectId, options) {
    return request({
      route: "api_projects_archive",
      params: { projectId },
      method: "POST",
      ...options,
    });
  },

  deleteProject(projectId, options) {
    return request({
      route: "api_projects_delete",
      params: { projectId },
      method: "DELETE",
      ...options,
    });
  },

  fetchProject(projectId, options) {
    return request({
      route: "api_projects_show",
      params: { projectId },
      ...options,
    });
  },

  createProject(type, input, options) {
    return request({
      route:
        "subscription" === type
          ? "api_projects_create_subscription"
          : "api_projects_create_intervention",
      method: "POST",
      body: { projectInput: toProjectInput(input) },
      ...options,
    });
  },

  updateProject(projectId, input, options) {
    return request({
      route: "api_projects_update",
      params: { projectId },
      method: "PUT",
      body: { projectInput: toProjectInput(input) },
      ...options,
    });
  },

  fetchJobs(projectId, options) {
    return request({
      route: "api_project_jobs_list",
      params: { projectId },
      ...options,
    });
  },

  fetchInterventions(projectId, options) {
    return request({
      route: "api_project_interventions_list",
      params: { projectId },
      ...options,
    });
  },

  fetchExpenseTypes(options) {
    return request({
      route: "api_projects_expense_types",
      ...options,
    });
  },

  fetchPaymentTypes(options) {
    return request({
      route: "api_client_payment_types",
      ...options,
    });
  },

  async fetchClientSubscriptions(clientId, options) {
    const projects = await request<ClientProjectRow[]>({
      route: "api_clients_projects",
      params: { clientId },
      query: { filter: "subscription", includeArchived: true },
      ...options,
    });

    return projects.map((project) => ({
      id: project.id,
      label: `${project.id} - ${project.displayName ?? ""}`.trim(),
      hint: project.statusLabel,
    }));
  },

  async fetchLocations(contactId, options) {
    const { items } = await request<LocationPage>({
      route: "api_locations_list",
      query: { contactId, perPage: LOCATION_PAGE_CAP },
      ...options,
    });

    return items.map((location) => ({
      id: location.id,
      label: location.displayName ?? location.name ?? String(location.id),
      hint: location.address,
    }));
  },

  async fetchBillingAddresses(contactId, options) {
    const addresses = await request<AddressRow[]>({
      route: "api_typedaddresses_list",
      query: { contactId },
      ...options,
    });

    return addresses.map((address) => ({
      id: address.id,
      label: address.displayAddress ?? address.label ?? String(address.id),
      hint: address.place,
    }));
  },

  async fetchClientContacts(clientId, options) {
    const client = await request<{ contacts?: ClientContactRow[] }>({
      route: "api_clients_show",
      params: { clientId },
      ...options,
    });

    return (client.contacts ?? []).map((contact) => ({
      id: contact.id,
      label: contact.contactDisplayName ?? String(contact.id),
      hint: contact.typeLabel,
      avatarUrl: contact.avatarUrl,
      hasPhoto: contact.hasPhoto,
      isOrganization: contact.isOrganization,
    }));
  },

  fetchBalanceSheet(projectId, options) {
    return request({
      route: "api_project_balance_sheet",
      params: { projectId },
      ...options,
    });
  },

  saveBalanceSheetBill(projectId, input, options) {
    return request({
      route: "api_project_balance_sheet_bill_update",
      params: { projectId },
      method: "PUT",
      body: { balanceSheetBillInput: input },
      ...options,
    });
  },

  sendBalanceSheetToBilling(projectId, input, options) {
    return request({
      route: "api_project_balance_sheet_send_to_billing",
      params: { projectId },
      method: "POST",
      body: { balanceSheetBillInput: input },
      ...options,
    });
  },

  closeBalanceSheet(projectId, input, options) {
    return request({
      route: "api_project_balance_sheet_close",
      params: { projectId },
      method: "POST",
      body: { balanceSheetBillInput: input },
      ...options,
    });
  },

  fetchClosureWeeks(projectId, year, prefill, options) {
    return request({
      route: "api_project_closure_weeks_show",
      params: { projectId, year },
      query: { prefill },
      ...options,
    });
  },

  saveClosureWeeks(projectId, year, input, options) {
    return request({
      route: "api_project_closure_weeks_update",
      params: { projectId, year },
      method: "PUT",
      body: { closureWeekYearInput: input },
      ...options,
    });
  },
};

function encodeCriteria(
  criteria: DataTableColumnFilter[] | undefined,
): string | undefined {
  if (undefined === criteria || 0 === criteria.length) {
    return undefined;
  }

  return JSON.stringify(
    criteria.map(({ id, operator, value, value2, values }) => ({
      column: id,
      operator,
      value,
      value2,
      values,
    })),
  );
}

type ClientProjectRow = {
  id: number;
  displayName: string | null;
  statusLabel: string | null;
};

type LocationRow = {
  id: number;
  name: string | null;
  displayName: string | null;
  typeLabel: string | null;
  address: string | null;
};

type LocationPage = {
  items: LocationRow[];
  total: number;
  page: number;
  perPage: number;
};

type AddressRow = {
  id: number;
  label: string | null;
  place: string | null;
  displayAddress: string | null;
};

export type ClientContactRow = {
  id: number;
  typeId: number | null;
  typeLabel: string | null;
  contactId: number | null;
  contactDisplayName: string | null;
  isOrganization: boolean | null;
  avatarUrl: string | null;
  hasPhoto: boolean;
};
